import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Facebook, Instagram, ShoppingBag, ArrowUpRight, CheckCircle2, Plus } from "lucide-react";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { usePlatformIntegrations } from "@/features/integrations/hooks/usePlatformIntegrations";
import { useEcommerceIntegration } from "@/features/integrations/hooks/useIntegrations";
import { itemVariants } from "./motion";
import { cn } from "@/lib/utils";

export function ConnectionStatusGrid() {
  const { connectedPlatforms, isLoading: isLoadingPlatforms } = usePlatformIntegrations();
  const { connections: storeConnections, isLoading: isLoadingStores } = useEcommerceIntegration();

  const isLoading = isLoadingPlatforms || isLoadingStores;

  const shopifyConn = storeConnections.find(
    (c: any) => (c.platform === "shopify" || c.platform === "woocommerce") && c.isActive
  );
  const isShopifyConnected = !!shopifyConn;

  const facebookAccounts = connectedPlatforms?.accounts?.facebook || [];
  const isFacebookConnected = facebookAccounts.length > 0;

  const instagramAccounts = connectedPlatforms?.accounts?.instagram || [];
  const isInstagramConnected = instagramAccounts.length > 0;

  const whatsappAccounts = connectedPlatforms?.accounts?.whatsapp || [];
  const isWhatsappConnected = whatsappAccounts.length > 0;

  const connections = [
    {
      name: "Shopify",
      fullName: shopifyConn?.storeName || (shopifyConn?.storeUrl ? shopifyConn.storeUrl.replace(/^https?:\/\//, "") : "Shopify Store"),
      category: "Storefront",
      isConnected: isShopifyConnected,
      subtitle: isShopifyConnected ? (shopifyConn?.storeName || "Active & Synced") : "Not connected",
      icon: ShoppingBag,
      badgeBg: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-800/50",
      dotClass: "bg-emerald-500",
    },
    {
      name: "Facebook",
      fullName: "Facebook Pages",
      category: "Messenger",
      isConnected: isFacebookConnected,
      subtitle: isFacebookConnected
        ? `${facebookAccounts.length} page${facebookAccounts.length > 1 ? "s" : ""} connected`
        : "Not connected",
      icon: Facebook,
      badgeBg: "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200/60 dark:border-blue-800/50",
      dotClass: "bg-blue-500",
    },
    {
      name: "Instagram",
      fullName: "Instagram Business",
      category: "Direct Messages",
      isConnected: isInstagramConnected,
      subtitle: isInstagramConnected
        ? `${instagramAccounts.length} account${instagramAccounts.length > 1 ? "s" : ""} connected`
        : "Not connected",
      icon: Instagram,
      badgeBg: "bg-pink-50 dark:bg-pink-950/40 text-pink-600 dark:text-pink-400 border-pink-200/60 dark:border-pink-800/50",
      dotClass: "bg-pink-500",
    },
    {
      name: "WhatsApp",
      fullName: "WhatsApp Business",
      category: "Messaging API",
      isConnected: isWhatsappConnected,
      subtitle: isWhatsappConnected
        ? `${whatsappAccounts.length} number${whatsappAccounts.length > 1 ? "s" : ""} active`
        : "Not connected",
      icon: MessageCircle,
      badgeBg: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-800/50",
      dotClass: "bg-emerald-500",
    },
  ];

  return (
    <motion.div variants={itemVariants} className="pb-8">
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        {/* Header Strip */}
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShoppingBag className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Platform & Channel Connections
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Connected Hub
            </span>
          </div>
          <Link
            to="/integrations"
            className="text-[10px] font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-0.5"
          >
            Manage All
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="grid w-full grid-cols-1 gap-2.5 p-3 sm:grid-cols-2 lg:grid-cols-4 bg-white/70 dark:bg-slate-900/70">
          {connections.map((connection) => {
            const IconComponent = connection.icon;
            const isConnected = connection.isConnected;

            return (
              <Link
                key={connection.name}
                to="/integrations"
                className="group flex items-center justify-between rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-850/60 p-2.5 transition-all duration-200 hover:border-primary/40 dark:hover:border-primary/50 hover:bg-white dark:hover:bg-slate-800 hover:shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <div
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border shadow-2xs transition-transform duration-200 group-hover:scale-105",
                      connection.badgeBg
                    )}
                  >
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="block text-xs font-semibold text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white transition-colors truncate">
                      {connection.fullName}
                    </span>
                    <span className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400 font-medium truncate">
                      <span
                        className={cn(
                          "h-1.5 w-1.5 shrink-0 rounded-full",
                          isConnected ? connection.dotClass : "bg-slate-300 dark:bg-slate-600"
                        )}
                      />
                      <span className="truncate">{isLoading ? "Checking..." : connection.subtitle}</span>
                    </span>
                  </div>
                </div>

                <div className="shrink-0 pl-1">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold transition-colors",
                      isConnected
                        ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800/50 group-hover:bg-emerald-100/70 dark:group-hover:bg-emerald-900/50"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700 group-hover:bg-slate-200/60 dark:group-hover:bg-slate-700"
                    )}
                  >
                    {isConnected ? (
                      <>
                        <CheckCircle2 className="h-2.5 w-2.5" />
                        Connected
                      </>
                    ) : (
                      <>
                        <Plus className="h-2.5 w-2.5" />
                        Connect
                      </>
                    )}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </Card>
    </motion.div>
  );
}
